import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Observable, OperatorFunction, Subject, filter, map, startWith, switchMap } from 'rxjs';
import { AnalyticsServicesNameEnum } from 'src/app/core/services/analytics/enums/analytics-services-name.enum';
import { FirebaseAnalyticsService } from 'src/app/core/services/analytics/firebase-analytics.service';
import {
  segmentObfuscatedFundingCodes,
  segmentObfuscatedSupportTypeCodes,
  segmentObfuscatedTypeOfFunding,
} from 'src/app/core/services/analytics/maps/segment-obfuscated-values';
import {
  AnalyticsDoubleTrackEvent,
  AnalyticsDoubleTrackEvents,
  AnalyticsDoubleTrackProperties,
} from 'src/app/core/services/analytics/models/analytics-double-track-properties.model';
import { AnalyticsEventGlobalProperties } from 'src/app/core/services/analytics/models/analytics-event-global-properties.model';
import { AnalyticsEventIdentifyProperties } from 'src/app/core/services/analytics/models/analytics-event-identify-properties';
import { AnalyticsEventProperties } from 'src/app/core/services/analytics/models/analytics-event-properties.model';
import { ActionEventParams } from 'src/app/core/services/analytics/models/firebase-action-event-params.model';
import { SegmentAnalyticsService } from 'src/app/core/services/analytics/segment-analytics.service';
import { obfuscateIdentifyEventSingleValue } from 'src/app/core/services/analytics/utils/obfuscating-data';
import { User } from 'src/app/shared/models/gql/user.model';
import { AccountService } from 'src/app/shared/services/account.service';
import { attemptLocalToInternationalMobileNumber } from 'src/app/shared/utilities/phone-number-format';
import { distinctUntilObjectChanged } from 'src/app/shared/utilities/rxjs/distinct-until-object-changed';

/**
 * This service enables to run different types of analytics related services (such as firebaseAnalyticsService via FE).
 */

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  // For storing initialisation status of the service to avoid multiple initialisations.
  private isInitialised = false;

  // For storing the queue of dialogues to be presented to end-user.
  private identifyQueueSubject: Subject<AnalyticsEventIdentifyProperties> = new Subject();

  // Empties the queue on request (typically during login and out)
  private resetQueueSubject: Subject<void> = new Subject();

  constructor(
    private accountService: AccountService,
    private firebaseAnalyticsService: FirebaseAnalyticsService,
    private segmentAnalyticsService: SegmentAnalyticsService,
    private titleCasePipe: TitleCasePipe,
  ) {
    // This subscribe is where the magic for the queue happens. Starting with null to get it started, events from the
    // identifyQueueSubject are read in order, with duplicates being ignored using distinctUntilObjectChanged. If a logout
    // occurs, resetQueueSubject will be triggered with next(), causing the queue to be thrown away and replaced with a
    // new one.
    this.resetQueueSubject
      .pipe(
        startWith(null),
        switchMap(() =>
          this.identifyQueueSubject.asObservable().pipe(this.obfuscateRequiredPII(), distinctUntilObjectChanged()),
        ),
      )
      .subscribe((eventProperties) => this.sendIdentify(eventProperties));
  }

  /**
   * Initialise Segment service for tracking by default.
   * This method should be called once on application startup.
   */
  initialise(): void {
    if (this.isInitialised) return;

    // Sets up the Segment JavaScript SDK service
    this.segmentAnalyticsService.initialise();

    // Whenever the user object changes, attempt to send a identify event. Duplicates will be thrown away / ignored
    this.accountService.user$
      .pipe(startWith(null), this.userModelFilter())
      .subscribe((properties) => this.identify(properties));

    this.isInitialised = true;
  }

  /**
   * Logged in User change callback
   * should be called when upstream services detect the logged in user has changed (logout or otherwise)
   * In particular, used to reset Identify queues and the Segment user if a logout occurs.
   */
  loggedInUserChanged(): void {
    // If the user is logged out it makes a Segment reset call to clear userId and anonymous_id
    // saved in local storage and cookies. We only need to do this for the JS Segment service. Calling it
    // if not JS SDK is running is safe as it internally checks before executing.
    this.segmentAnalyticsService.reset();

    // Also delete any remaining identify events in queue and start fresh for a new user.
    this.resetQueueSubject.next();
  }

  /**
   * Global Clicked Event
   *
   * @param properties - The properties of the event.
   * @param serviceName - The name of the analytics service to call
   */
  clicked(
    properties: AnalyticsEventGlobalProperties,
    serviceName: AnalyticsServicesNameEnum = AnalyticsServicesNameEnum.SEGMENT_SERVICE,
  ): void {
    this.track('Clicked', { ...properties }, serviceName);
  }

  /**
   * The track method to call different track methods in different analytics services.
   * The default case: If no service name is provided, it will call all analytics services.
   * Please remember to add any new analytic service track call to the default case whenever adding a new service.
   *
   * @param event - The name of the tracking event.
   * @param properties - A dictionary of properties for the event.
   * @param serviceName - The name of the service you want to call.
   */
  track(
    event: string,
    properties:
      | ActionEventParams
      | AnalyticsEventProperties
      | AnalyticsDoubleTrackProperties
      | AnalyticsEventGlobalProperties = {},
    serviceName?: AnalyticsServicesNameEnum,
  ): void {
    if (!this.isInitialised) {
      console.warn('Analytics Service is not initialised yet.');
    }

    this.handleEventing(
      serviceName,
      () => this.firebaseAnalyticsService.logClick(event, properties),
      () => this.segmentAnalyticsService.track(event, properties),
      'Unable to send track analytics event',
    );
  }

  /**
   * The Double Track method is used when a single track event needs to be applied to both the current target and another user.
   * Only supported by segment proxy service.
   *
   * @param event - The name of the tracking event.
   * @param properties - A dictionary of properties for the event.
   * @example
   * properties: {
   *   'current_target': {
   *     'property1': 'value1',
   *   },
   *   'other_user': {
   *     'user_id': 'uuid',
   *     'other_user_property2': 'value2'
   *   }
   * }
   */
  doubleTrack(event: string, properties: AnalyticsDoubleTrackProperties): void {
    if (!this.isInitialised) {
      console.warn('Analytics Service is not initialised yet.');
    }

    const { currentUserEvent, otherUserEvent } = this.buildDoubleTrackEvents(event, properties);

    // Track the current user event
    this.segmentAnalyticsService.track(currentUserEvent.name, currentUserEvent.properties, currentUserEvent.options);

    // Track the other user event
    this.segmentAnalyticsService.track(otherUserEvent.name, otherUserEvent.properties, otherUserEvent.options);
  }

  /**
   * The buildDoubleTrackEvents method is used to build an array of events to be tracked.
   *
   * @param name - The name of the tracking event.
   * @param properties - A dictionary of properties for the event.
   * @returns - AnalyticsDoubleTrackEvents.
   */
  private buildDoubleTrackEvents(name: string, properties: AnalyticsDoubleTrackProperties): AnalyticsDoubleTrackEvents {
    // Extract properties for new events, excluding some specific properties
    const { current_target, other_user } = properties;
    const other_member_type = current_target.initiated_by_user_type === 'client' ? 'worker' : 'client';
    // Create the current user event with the original userId
    const currentUserEvent: AnalyticsDoubleTrackEvent = {
      name,
      properties: current_target,
      options: {},
    };
    // Create the other user event with the otherUserUuid as userId
    const otherUserEvent: AnalyticsDoubleTrackEvent = {
      name,
      properties: {
        ...other_user,
        member_type: other_member_type,
      },
      options: {},
    };
    return { currentUserEvent, otherUserEvent };
  }

  /**
   * The page method to call different page methods in different analytics services.
   * The default case: If no service name is provided, it will call all analytics services.
   * Please remember to add any new analytic service page call to the default case whenever adding a new service.
   *
   * @param name - The name of the page.
   * @param properties - A dictionary of properties for the event.
   * @param serviceName - The name of the service you want to call.
   */
  page(name: string, properties?: AnalyticsEventProperties, serviceName?: AnalyticsServicesNameEnum): void {
    const globalProperties: AnalyticsEventGlobalProperties = {
      ...properties,
      object_type: 'Page',
      object_target: name,
    };

    if (!this.isInitialised) {
      console.warn('Analytics Service is not initialised yet.');
    }

    this.handleEventing(
      serviceName,
      () => this.firebaseAnalyticsService.logPageEvent(name),
      () => this.segmentAnalyticsService.page(name, globalProperties),
      'Unable to send page analytics event',
    );
  }

  /**
   * Calls a Segment Identify call on the Segment SDK.
   * Will not check if initialised as this method only adds to a RxJs queue.
   *
   * If the user is not yet logged in and does not have a valid auth token, the call will silently be ignored.
   *
   * @param properties - A dictionary of properties to identify on the user.
   */
  identify(properties: AnalyticsEventIdentifyProperties = {}): void {
    // If there is no user id avaliable we dont want to send a identify event to queue
    // At this stage silently failing is ok
    const userId = this.accountService.getUser()?.custom?.user_uuid;
    if (userId) {
      this.identifyQueueSubject.next(properties);
    }
  }

  /**
   * Maps known user types and maps them to the prefered string value in segment. If no
   * prefered value is known, or the default value is ok, the provided value is simply
   * returned.
   *
   * @param userType - A user type, possible from a known list to be re-mapped.
   * @returns The user type in a segment friendly format.
   */
  accountTypeMapper(userType: string): string {
    switch (userType) {
      case 'consumer':
        return 'client';
      case 'carer':
        return 'support provider';
      default:
        // All other user types are presumed to be acceptable (aka coordinators, admins, organisations)
        return userType;
    }
  }

  /**
   * The handleTracking method is used to call different methods in different analytics services.
   *
   * @private
   * @param {(AnalyticsServicesNameEnum | undefined)} serviceName - The name of the service you want to call.
   * @param {() => void} firebaseMethod - The method to call for the firebase service.
   * @param {() => void} segmentMethod - The method to call for the segment service.
   * @param {string} errorMessage - The error message to log when error.
   */
  private handleEventing(
    serviceName: AnalyticsServicesNameEnum | undefined,
    firebaseMethod: () => void,
    segmentMethod: () => any,
    errorMessage: string,
  ): void {
    try {
      switch (serviceName) {
        case AnalyticsServicesNameEnum.FIREBASE_ANALYTICS_SERVICE:
          firebaseMethod();
          break;
        case AnalyticsServicesNameEnum.SEGMENT_SERVICE:
          segmentMethod();
          break;
        default:
          // If no service name is provided. it will call all analytics services.
          firebaseMethod();
          segmentMethod();
          break;
      }
    } catch (error) {
      // Log the error with the specific error message
      console.error(errorMessage, error);
    }
  }

  /**
   * Handles actually sending Identify events from the RxJs Queue. The response from the server
   * is currently ignored for the most part however, if a error is thrown it will be logged.
   *
   * @private
   * @param properties - the events properties.
   */
  private sendIdentify(properties: AnalyticsEventIdentifyProperties): void {
    try {
      this.segmentAnalyticsService.identify(properties);
    } catch (error) {
      // No need to alert the user, just log the error for tracking purposes
      console.error('Unable to send Segment "identify" event', error);
    }
  }

  /**
   * Custom RxJs operator, used to assist in subscribing to the user model. Transforms the emitted
   * model into a AnalyticsEventIdentifyProperties object and then filters out any empty objects
   * (i.e. incase the fields emitted from the object are empty, or empty strings).
   *
   * @private
   * @returns An Observable<AnalyticsEventIdentifyProperties> which we want to add to identify queue.
   */
  private userModelFilter<T extends User>(): OperatorFunction<T, AnalyticsEventIdentifyProperties> {
    return (source$: Observable<T>): Observable<AnalyticsEventIdentifyProperties> =>
      source$.pipe(
        // Ignore any call that does not contain data, a Identify should always atleast contain a property from below.
        filter((data) => !!data),
        // We only want to return a object containing the fields identify cares about.
        // We also want to make sure we arent overriding existing values with empty strings
        map((data) => {
          const userType = this.accountTypeMapper(this.accountService.getUserType());
          const analyticsEventIdentifyProperties: AnalyticsEventIdentifyProperties = {};
          // Global traits
          if (data.firstName)
            analyticsEventIdentifyProperties.first_name = this.titleCasePipe.transform(data.firstName);
          if (data.lastName) analyticsEventIdentifyProperties.last_name = this.titleCasePipe.transform(data.lastName);
          if (data.email) analyticsEventIdentifyProperties.email = data.email;
          if (data.contactPhone)
            analyticsEventIdentifyProperties.phone = attemptLocalToInternationalMobileNumber(data.contactPhone);

          if (data.postcode || data.state || data.addressLine2) {
            analyticsEventIdentifyProperties.address = {};
            // postal_code is used over postcode as postal_code is expected by segments standardised traits
            if (data.postcode) analyticsEventIdentifyProperties.address.postal_code = data.postcode;
            if (data.state) analyticsEventIdentifyProperties.address.state = data.state;
            if (data.addressLine2) analyticsEventIdentifyProperties.address.suburb = data.addressLine2;
          }

          // Client specific traits.
          if (data.clientTypeName) analyticsEventIdentifyProperties.client_type = data.clientTypeName.toLowerCase();

          // We only want to append user_type if there is actual data in this Identify call.
          if (!!userType && Object.keys(analyticsEventIdentifyProperties).length)
            analyticsEventIdentifyProperties.user_type = userType;

          return analyticsEventIdentifyProperties;
        }),
        // Only emit a event if it has properties
        filter((analyticsEvent) => Object.keys(analyticsEvent).length > 0),
      );
  }

  /**
   * Custom RxJs operator, used to ensure any PII we are NOT allowed to send to Segment direct does not leak through into a
   * clients Identify call. PII of these fields will either be replaced with obscured mappings, or removed all together from the
   * call if we cannot. Any empty events post this process will be filtered out of the queue.
   *
   * @private
   * @returns A safe to emit Identify event, else nothing if the event is now empty.
   */
  private obfuscateRequiredPII<T extends AnalyticsEventIdentifyProperties>(): OperatorFunction<
    T,
    AnalyticsEventIdentifyProperties
  > {
    return (source$: Observable<T>): Observable<AnalyticsEventIdentifyProperties> =>
      source$.pipe(
        map((event) => {
          let obfuscatedEvent = event;

          if (obfuscatedEvent.funding_type) {
            obfuscatedEvent = obfuscateIdentifyEventSingleValue(
              'funding_type',
              obfuscatedEvent,
              segmentObfuscatedFundingCodes,
            );
          }

          //  TODO(ES-37908): Remove, as question is removed from signup flow
          if (obfuscatedEvent.support_type) {
            obfuscatedEvent = obfuscateIdentifyEventSingleValue(
              'support_type',
              obfuscatedEvent,
              segmentObfuscatedSupportTypeCodes,
            );
          }

          if (obfuscatedEvent.type_of_funding) {
            obfuscatedEvent = obfuscateIdentifyEventSingleValue(
              'type_of_funding',
              obfuscatedEvent,
              segmentObfuscatedTypeOfFunding,
            );
          }

          // After deleting fields we cannot obscure that are required, if the only remaining field is user_type, delete it
          // This will prevent pointless identify calls being made as empty calls are filtered out
          if (Object.keys(obfuscatedEvent).length === 1 && !!obfuscatedEvent.user_type) {
            delete obfuscatedEvent.user_type;
          }

          return obfuscatedEvent;
        }),
        // If after obfuscating (and removing fields if of a unknown type), filter out empty events again
        filter((event) => Object.keys(event).length > 0),
      );
  }
}
